import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Form,
  FormButton,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useExamDistributionByMonth } from '../../providers';
import { ExaminationCourseFormList } from '../examination-course-form-list';

const ClearExamAssignmentFormSchema = z.object({
  examCourseCodes: z.array(z.string()).min(1),
});

type ClearExamAssignmentFormInput = z.infer<typeof ClearExamAssignmentFormSchema>;

interface ClearExamAssignmentFormModalProps {
  open: boolean;
  onClose: () => void;
}

export default function ClearExamAssignmentFormModal(
  props: Readonly<ClearExamAssignmentFormModalProps>,
) {
  const { open, onClose } = props;

  const { showSnackbar } = useSnackbar();

  const { unassignExams } = useExamDistributionByMonth();

  const formContext = useForm<ClearExamAssignmentFormInput>({
    resolver: zodResolver(ClearExamAssignmentFormSchema),
    defaultValues: {
      examCourseCodes: [],
    },
  });

  const handleSubmit = formContext.handleSubmit(async (data) => {
    const { examCourseCodes } = data;
    await unassignExams(examCourseCodes, {
      onCompleted: () => {
        showSnackbar({
          message: `ล้างค่าการมอบหมายชุดข้อสอบเรียบร้อยแล้ว`,
          variant: 'success',
        });
        handleClose();
      },
      onError: () => {
        showSnackbar({
          message: `ไม่สามารถล้างค่าการมอบหมายชุดข้อสอบได้`,
          variant: 'error',
        });
      },
    });
  });

  const totalSelectedCourses = formContext.watch('examCourseCodes')?.length ?? 0;
  const isFormSubmitting = formContext.formState.isSubmitting;

  const handleClose = () => {
    if (isFormSubmitting) return;
    onClose();
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={open}
        maxWidth='lg'
        onClose={handleClose}
        slotProps={{
          paper: { component: Form, onSubmit: handleSubmit },
          transition: { onExited: () => formContext.reset() },
        }}
      >
        <DialogTitle loading={isFormSubmitting} onClose={onClose}>
          Clear Assignment
        </DialogTitle>
        <DialogContent dividers>
          <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
            <Text variant='h4'>
              เลือกหลักสูตรที่ต้องการล้างค่าการมอบหมายชุดข้อสอบ ({totalSelectedCourses})
            </Text>
            <ExaminationCourseFormList name='examCourseCodes' />
          </Flex>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            color='secondary'
            onClick={onClose}
            disabled={formContext.formState.isSubmitting}
          >
            Cancel
          </Button>
          <FormButton label='Clear' />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
